<script setup lang="ts">
import * as yup from "yup"
import { useOperationStore } from "~/stores/operation.store"
import { cloneDeep } from "lodash-es"
import type { components } from "assets/api/openapi"
import ForeignTraningTemplateSelector from "@/components/workflows/pug/work-steps/03-ausbildungsvergleich/foreign-tranings/foreign-traning-template-selector.vue"
import ForeignTraningGroupsTable from "@/components/workflows/pug/work-steps/03-ausbildungsvergleich/foreign-tranings/foreign-traning-groups-table.vue"
import ForeignTrainingTemplateEditor from "@/components/workflows/pug/work-steps/03-ausbildungsvergleich/foreign-tranings/foreign-training-template-editor.vue"

const operationStore = useOperationStore()
const appApi = useAppApi()
const slideOver = useSlideover()

const FOREIGN_TRAINING_COMMENT_MAX_LENGTH = 1000

const trainingTemplateSchema = yup.object().shape({
  templateId: yup.number().optional(),
  templateName: yup.string().required("Template Name ist erforderlich"),
  trainingGroups: yup
    .mixed<
      Record<string, components["schemas"]["TrainingAssessment"][] | undefined>
    >()
    .optional(),
  comment: yup.string().optional(),
})

const schema = yup.object().shape({
  foreignTraining: trainingTemplateSchema.optional(),
  equivalence: yup.boolean().required("Pflichtfeld"),
})

type Schema = yup.InferType<typeof schema>

const currentWorkStepData = computed<Schema>(
  () => (operationStore.currentWorkStep?.data ?? {}) as unknown as Schema
)

const state = reactive<Schema>(cloneDeep(currentWorkStepData.value))

const lastSavedForeignTraining = ref(cloneDeep(state.foreignTraining))

const foreignTrainings = ref<
  components["schemas"]["ForeignTrainings"] | undefined
>(undefined)
const isLoading = ref(false)

const isCustomTrainingUpToDate = computed(
  () => foreignTrainings.value?.upToDate ?? true
)

const areAllRefreshTrainingHoursCountFilled = computed<boolean>(() => {
  const trainingGroups = state.foreignTraining?.trainingGroups

  if (!trainingGroups) {
    return false
  }

  const allAssessments = Object.values(trainingGroups)
    .flat()
    .filter(Boolean) as components["schemas"]["TrainingAssessment"][]
  if (allAssessments.length === 0) {
    return false
  }
  return allAssessments.every((assessment) => {
    const value = assessment.refreshTrainingHoursCount
    return value !== undefined && String(value) !== ""
  })
})

const saveButtonDisabledReason = computed<string>(() => {
  if (!areAllRefreshTrainingHoursCountFilled.value) {
    return "Bitte füllen Sie alle Nachschulung Felder aus, um das Vergleich als Vorlage zu speichern."
  }

  const isLastSavedForeignTrainingSelected =
    state.foreignTraining?.templateName ===
    lastSavedForeignTraining.value?.templateName

  if (isLastSavedForeignTrainingSelected && !isCustomTrainingUpToDate.value) {
    return "Die ausgewählte Vorlage ist nicht mehr aktuell."
  }

  return ""
})

const isSaveAsTemplateDisabled = computed<boolean>(() =>
  Boolean(saveButtonDisabledReason.value)
)

const fetchForeignTrainings = async () => {
  isLoading.value = true
  try {
    if (!operationStore.currentWorkStepId) {
      return
    }

    const { status, data } = await appApi.getForeignTrainingTemplates(
      operationStore.currentWorkStepId
    )
    if (status === 200 && data) {
      foreignTrainings.value = data
    } else {
      showToast("danger", {
        description: `Die Arbeitsschrittdaten konnten nicht geladen werden`,
      })
    }
  } catch (error) {
    showError({
      statusCode: 500,
    })
  } finally {
    isLoading.value = false
  }
}

const openForeignTrainingTemplateEditor = () => {
  if (!state.foreignTraining || isSaveAsTemplateDisabled.value) {
    return
  }
  slideOver.open(ForeignTrainingTemplateEditor, {
    foreignTraining: state.foreignTraining,
    onRefreshData: () => {
      fetchForeignTrainings()
    },
  })
}

const preselectForeignTraining = () => {
  if (!state.foreignTraining) {
    state.foreignTraining = foreignTrainings.value?.customTraining
  }
}

onMounted(async () => {
  await fetchForeignTrainings()
  preselectForeignTraining()

  await nextTick()
  operationStore.unsavedChanges = false
})

const templateOptions = computed<components["schemas"]["TrainingTemplate"][]>(
  () =>
    [
      lastSavedForeignTraining.value,
      foreignTrainings.value?.customTraining,
      ...(foreignTrainings.value?.foreignTrainingTemplates ?? []),
    ].filter(
      (template) => template !== undefined
    ) as components["schemas"]["TrainingTemplate"][]
)

</script>

<template>
  <IffOperationWorkStepFormV2 :state="state" :schema="schema">
    <template #above-form>
      <IffReviewsSection
        v-if="operationStore.currentWorkStep"
        :work-step-id="operationStore.currentWorkStep?.id"
        additional-headline="Ausbildungsvergleich"
      />
    </template>

    <template v-if="!isLoading">
      <ForeignTraningTemplateSelector
        v-model="state.foreignTraining"
        :options="templateOptions"
        :is-up-to-date="isCustomTrainingUpToDate"
      />

      <template
        v-if="
          state.foreignTraining &&
          foreignTrainings?.foreignTrainingConfiguration
        "
      >
        <ForeignTraningGroupsTable
          :template="state.foreignTraining"
          :configuration-type="foreignTrainings.foreignTrainingConfiguration"
        />

        <UFormGroup
          name="foreignTraining.comment"
          label="Vermerk zum Ausbildungsvergleich"
          :help="`Noch ${
            FOREIGN_TRAINING_COMMENT_MAX_LENGTH -
            (state.foreignTraining.comment?.length ?? 0)
          } Zeichen verfügbar`"
          class="max-w-[600px] mt-8"
        >
          <UTextarea
            v-model="state.foreignTraining.comment"
            :maxlength="FOREIGN_TRAINING_COMMENT_MAX_LENGTH"
            resize
          />
        </UFormGroup>

        <IffContentSection headline="Vorlage speichern" :is-last-child="false">
          <UTooltip
            :text="saveButtonDisabledReason || undefined"
            :prevent="!isSaveAsTemplateDisabled"
          >
            <UButton
              @click="openForeignTrainingTemplateEditor()"
              variant="outline"
              :disabled="isSaveAsTemplateDisabled"
              :aria-label="
                isSaveAsTemplateDisabled
                  ? `Vergleich als Vorlage speichern - ${saveButtonDisabledReason}`
                  : 'Vergleich als Vorlage speichern'
              "
            >
              Vergleich als Vorlage speichern
            </UButton>
          </UTooltip>
        </IffContentSection>

        <IffContentSection
          headline="Prüfung Gleichwertigkeit"
          :is-last-child="true"
        >
          <IffDualRadioGroup
            name="equivalence"
            v-model="state.equivalence"
            legend="Ist Gleichwertigkeit gegeben?"
            required
          />
        </IffContentSection>
      </template>

      <IffContentSection
        v-else
        headline="Keine Vorlage ausgewählt"
        :is-last-child="true"
      >
        <div class="text-center py-8 text-gray-500">
          Bitte wählen Sie eine Vorlage aus, um fortzufahren.
        </div>
      </IffContentSection>
    </template>
  </IffOperationWorkStepFormV2>
</template>
